import React, { useCallback, useEffect, useRef, useState } from 'react';

import Popup, { type TriggerProps } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';

type HoverPopoverProps = {
	children: (
		args: TriggerProps & {
			onKeyDown: React.KeyboardEventHandler;
			onMouseEnter: React.MouseEventHandler;
			onMouseLeave: React.MouseEventHandler;
		},
	) => React.ReactNode;
	closeDelayMs?: number;
	content: React.ReactNode;
	openDelayMs?: number;
	placement?: React.ComponentProps<typeof Popup>['placement'];
};

const HoverPopover = ({
	content,
	placement = 'bottom',
	openDelayMs = 200,
	closeDelayMs = 80,
	children,
}: HoverPopoverProps): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState(false);
	const openTimeout = useRef<number | null>(null);
	const closeTimeout = useRef<number | null>(null);

	useEffect(() => {
		return () => {
			if (openTimeout.current) {
				window.clearTimeout(openTimeout.current);
			}
			if (closeTimeout.current) {
				window.clearTimeout(closeTimeout.current);
			}
		};
	}, []);

	const onMouseEnter = useCallback(() => {
		if (closeTimeout.current) {
			window.clearTimeout(closeTimeout.current);
			closeTimeout.current = null;
		}
		openTimeout.current = window.setTimeout(() => setIsOpen(true), openDelayMs);
	}, [openDelayMs]);

	const onMouseLeave = useCallback(() => {
		if (openTimeout.current) {
			window.clearTimeout(openTimeout.current);
			openTimeout.current = null;
		}
		closeTimeout.current = window.setTimeout(() => setIsOpen(false), closeDelayMs);
	}, [closeDelayMs]);

	const onKeyDown = useCallback<React.KeyboardEventHandler>((e) => {
		if (e.key === ' ' || e.key === 'Enter') {
			e.preventDefault();
			setIsOpen((prev) => !prev);
			return;
		}
	}, []);

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement={placement}
			content={() => (
				<Box onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
					{content}
				</Box>
			)}
			trigger={(triggerProps) =>
				children({
					onKeyDown,
					onMouseEnter,
					onMouseLeave,
					...triggerProps,
				})
			}
			shouldUseCaptureOnOutsideClick
			shouldRenderToParent
			shouldReturnFocus
		/>
	);
};

export default HoverPopover;
